import type { UpdateTutorBillingPayload } from './interfaces'

import { useTutorBillingReturnStore, TUTOR_BILLING_BACK_QUERY, resolveTutorBillingReturnPath } from '@features/tutor-billing-return'
import { useListsStore } from '@shared/model'
import { urlToFile } from '@shared/lib/url'
import { useToast } from '@shared/ui/toast'

import { TUTOR_LEGAL_FIELD_MAP } from '../../config/tutor-legal'
import { useUpdateTutorBillingApi } from '../../api/legal/billing-update.hook'
import { useTutorLegalStore } from './store'

interface SectionTutorLegalReturn {
  save: () => void
}

export const useSectionTutorLegal = (): SectionTutorLegalReturn => {
  const route = useRoute()
  const tutorId = route.params.id as string

  const tutorLegalStore = useTutorLegalStore()
  const listsStore = useListsStore()
  const tutorBillingReturnStore = useTutorBillingReturnStore()
  const toast = useToast()

  // Derived from TUTOR_LEGAL_FIELD_MAP — the same map hydrates the form in the
  // store, so a field added there is automatically included in the payload.
  const buildPayload = (): UpdateTutorBillingPayload => {
    const form = tutorLegalStore.getForm
    const payload: Record<string, Record<string, string>> = {
      personal: {},
      document: {},
      payment: {},
    }

    TUTOR_LEGAL_FIELD_MAP.forEach(({ form: formKey, section, api }) => {
      if (formKey === 'account_holder_name' && !form[formKey]) return
      payload[section]![api] = form[formKey]
    })

    return payload as unknown as UpdateTutorBillingPayload
  }

  // The update endpoint replaces the whole document pack, so every file we want
  // to keep must be re-sent: already-uploaded files (re-fetched as File) + new ones.
  const collectFiles = async (): Promise<File[]> => {
    const existingFiles = tutorLegalStore.getExistingFiles

    const existing = await Promise.all(
      existingFiles.map(file =>
        urlToFile(file.download_link, file.filename, file.mimetype),
      ),
    )

    // A re-dropped copy of an already-uploaded file (same name + size) would end
    // up twice in the replaced pack — send only genuinely new files.
    const existingKeys = new Set(existingFiles.map(file => `${file.filename}:${file.filesize}`))
    const pending = tutorLegalStore.getPendingFiles
      .map(item => item.file)
      .filter(file => !existingKeys.has(`${file.name}:${file.size}`))

    return [...existing, ...pending]
  }

  const submit = async (): Promise<void> => {
    const accountType = listsStore.getTutorBillingAccountType(tutorLegalStore.getForm.payment_service)

    if (!tutorLegalStore.validate(accountType)) {
      toast.error('Please fill in all required fields')
      return
    }

    let files: File[]
    try {
      files = await collectFiles()
    } catch {
      // Download links are signed with a 1h TTL — a stale page is the usual cause.
      toast.error('Failed to prepare uploaded files — please reload the page and try again')
      return
    }

    await useUpdateTutorBillingApi({
      tutorId,
      payload: buildPayload(),
      files,
      onSuccess: () => {
        tutorLegalStore.resetPending()
        if (route.query.back === TUTOR_BILLING_BACK_QUERY) void navigateTo(resolveTutorBillingReturnPath(tutorBillingReturnStore.getReturnPath))
      },
    })
  }

  const save = (): void => {
    void submit()
  }

  return {
    save,
  }
}
